/**
 * aiSettingsApi.js — Chatwoot frontend API client for AI settings management
 *
 * PATCH TARGET:
 *   app/javascript/dashboard/api/aiSettings.js  (NEW FILE)
 *
 * Calls the mention-bridge service to read/write Evolution chatbot configurations.
 * Only accessible to Chatwoot administrators.
 */

const MENTION_BRIDGE_BASE = window.MENTION_BRIDGE_URL || '/mention-bridge';

/**
 * Get Chatwoot user access token from auth store.
 */
function getAuthToken() {
  // Chatwoot stores auth data in the 'cw_d_*' cookie or in the Vuex store.
  // In patched components, we have access to `this.$store` or authToken prop.
  // This helper reads from the cookie as a fallback.
  const match = document.cookie.match(/(?:^|;\s*)cw_d_[^=]+=([^;]+)/);
  if (match) {
    try {
      const data = JSON.parse(decodeURIComponent(match[1]));
      return data.access_token || '';
    } catch { /* ignore */ }
  }
  // Fallback: check window.__STORE__ or localStorage
  try {
    const stored = Object.keys(localStorage)
      .filter(k => k.startsWith('cw_d_'))
      .map(k => { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } })
      .find(v => v && v.access_token);
    if (stored) return stored.access_token;
  } catch { /* ignore */ }
  return '';
}

/**
 * Internal fetch helper with auth.
 */
async function apiFetch(path, options = {}) {
  const token = options.token || getAuthToken();
  const url = `${MENTION_BRIDGE_BASE}/api/ai-settings${path}`;

  const resp = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
    credentials: 'same-origin',
  });

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    const err = new Error(body.error || `AI Settings API error: ${resp.status}`);
    err.status = resp.status;
    throw err;
  }

  return resp.json();
}

/**
 * Fetch all AI bot configurations for an instance.
 * @param {string} instanceName
 * @param {string} [token] - Optional explicit auth token
 * @returns {Promise<{instanceName, integrations, settings}>}
 */
export async function fetchAiSettings(instanceName, token) {
  return apiFetch(`/${encodeURIComponent(instanceName)}`, { token });
}

/**
 * Fetch status/overview of AI integrations for an instance.
 * @param {string} instanceName
 * @param {string} [token]
 */
export async function fetchAiStatus(instanceName, token) {
  return apiFetch(`/${encodeURIComponent(instanceName)}/status`, { token });
}

/**
 * Update a specific bot configuration.
 * @param {string} instanceName
 * @param {string} integration - openai, dify, n8n, etc.
 * @param {string} botId
 * @param {object} data - Partial fields to update
 * @param {string} [token]
 */
export async function updateAiBot(instanceName, integration, botId, data, token) {
  return apiFetch(
    `/${encodeURIComponent(instanceName)}/${integration}/${botId}`,
    { method: 'PUT', body: JSON.stringify(data), token }
  );
}

/**
 * Update integration-level settings (defaults, fallback).
 * @param {string} instanceName
 * @param {string} integration
 * @param {object} data
 * @param {string} [token]
 */
export async function updateAiSettings(instanceName, integration, data, token) {
  return apiFetch(
    `/${encodeURIComponent(instanceName)}/${integration}/settings`,
    { method: 'PATCH', body: JSON.stringify(data), token }
  );
}

/**
 * Fetch metadata: list of integrations and editable fields.
 * @param {string} [token]
 */
export async function fetchAiMeta(token) {
  return apiFetch('/', { token });
}
